'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { clearFilters, fetchFaturas, selectFaturas, selectFaturasError, selectFaturasFilters, selectFaturasLoading, selectFaturasSummary, setFilters } from '../store';
import type { Fatura, FaturaListFilters } from '../types';
interface UseFaturasListResult {
  faturas: Fatura[];
  filteredFaturas: Fatura[];
  summary: ReturnType<typeof selectFaturasSummary>;
  filters: FaturaListFilters;
  loading: boolean;
  error: string | null;
  isFiltering: boolean;
  refresh: () => void;
  updateFilters: (patch: Partial<FaturaListFilters>) => void;
  resetFilters: () => void;
}
const aplicarFiltros = (faturas: Fatura[], filters: FaturaListFilters): Fatura[] => {
  const buscaNormalizada = filters.busca.trim().toLowerCase();
  return faturas.filter(fatura => {
    if (filters.status.length > 0 && !filters.status.includes(fatura.status)) return false;
    if (filters.tipoGeracao.length > 0 && !filters.tipoGeracao.includes(fatura.tipoGeracao)) {
      return false;
    }
    if (filters.tipoCliente.length > 0 && !filters.tipoCliente.includes(fatura.tipoCliente)) {
      return false;
    }
    if (filters.somenteComErroEmissao && fatura.status !== 'ERRO_EMISSAO') return false;
    if (buscaNormalizada) {
      const matchTenant = fatura.tenantNome.toLowerCase().includes(buscaNormalizada);
      const matchNumero = fatura.numero.toLowerCase().includes(buscaNormalizada);
      const matchProduto = (fatura.produtoNome ?? '').toLowerCase().includes(buscaNormalizada);
      if (!matchTenant && !matchNumero && !matchProduto) return false;
    }
    const vencimentoDate = fatura.dataVencimento.slice(0, 10);
    if (filters.vencimentoInicio && vencimentoDate < filters.vencimentoInicio) return false;
    if (filters.vencimentoFim && vencimentoDate > filters.vencimentoFim) return false;
    const emissaoDate = fatura.dataEmissao.slice(0, 10);
    if (filters.emissaoInicio && emissaoDate < filters.emissaoInicio) return false;
    if (filters.emissaoFim && emissaoDate > filters.emissaoFim) return false;
    return true;
  });
};
export function useFaturasList() {
  const $ = _c(33);
  const dispatch = useAppDispatch();
  const faturas = useAppSelector(selectFaturas);
  const loading = useAppSelector(selectFaturasLoading);
  const error = useAppSelector(selectFaturasError);
  const summary = useAppSelector(selectFaturasSummary);
  const filters = useAppSelector(selectFaturasFilters);
  let t0;
  let t1;
  if ($[0] !== dispatch) {
    t0 = () => {
      dispatch(fetchFaturas());
    };
    t1 = [dispatch];
    $[0] = dispatch;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== faturas || $[4] !== filters) {
    t2 = aplicarFiltros(faturas, filters);
    $[3] = faturas;
    $[4] = filters;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const filteredFaturas = t2;
  let t3;
  if ($[6] !== filters.busca || $[7] !== filters.emissaoFim || $[8] !== filters.emissaoInicio || $[9] !== filters.somenteComErroEmissao || $[10] !== filters.status.length || $[11] !== filters.tipoCliente || $[12] !== filters.tipoGeracao || $[13] !== filters.vencimentoFim || $[14] !== filters.vencimentoInicio) {
    t3 = filters.status.length > 0 || filters.tipoGeracao.length > 0 || filters.tipoCliente.length > 0 || filters.busca.trim().length > 0 || filters.vencimentoInicio !== null || filters.vencimentoFim !== null || filters.emissaoInicio !== null || filters.emissaoFim !== null || filters.somenteComErroEmissao;
    $[6] = filters.busca;
    $[7] = filters.emissaoFim;
    $[8] = filters.emissaoInicio;
    $[9] = filters.somenteComErroEmissao;
    $[10] = filters.status.length;
    $[11] = filters.tipoCliente;
    $[12] = filters.tipoGeracao;
    $[13] = filters.vencimentoFim;
    $[14] = filters.vencimentoInicio;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const isFiltering = t3;
  let t4;
  if ($[16] !== dispatch) {
    t4 = () => {
      dispatch(fetchFaturas());
    };
    $[16] = dispatch;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const refresh = t4;
  let t5;
  if ($[18] !== dispatch) {
    t5 = patch => {
      dispatch(setFilters(patch));
    };
    $[18] = dispatch;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const updateFilters = t5;
  let t6;
  if ($[20] !== dispatch) {
    t6 = () => {
      dispatch(clearFilters());
    };
    $[20] = dispatch;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const resetFilters = t6;
  let t7;
  if ($[22] !== error || $[23] !== faturas || $[24] !== filteredFaturas || $[25] !== filters || $[26] !== isFiltering || $[27] !== loading || $[28] !== refresh || $[29] !== resetFilters || $[30] !== summary || $[31] !== updateFilters) {
    t7 = {
      faturas,
      filteredFaturas,
      summary,
      filters,
      loading,
      error,
      isFiltering,
      refresh,
      updateFilters,
      resetFilters
    };
    $[22] = error;
    $[23] = faturas;
    $[24] = filteredFaturas;
    $[25] = filters;
    $[26] = isFiltering;
    $[27] = loading;
    $[28] = refresh;
    $[29] = resetFilters;
    $[30] = summary;
    $[31] = updateFilters;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  return t7;
}